import React, { useState } from 'react'
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, Link } from '@mui/material'
import {
  AdvancedFinancialEligibilityCheckFragment,
  GetAdvancedEligibilityLookupInputsQuery,
  useAdvancedFinancialEligibilityLookupMutation,
  useGetAdvancedEligibilityLookupInputsQuery,
} from '../../../../api/advancedFinancialEligibilityLookup.generated'
import { describeFailure } from '../../../../api/failureReason'
import DialogHeader from '../../../../components/DialogHeader'
import FailureMessage from '../../../../components/FailureMessage'
import PrefillLoading from '../PrefillLoading'
import { useDisabledInPreview } from '../../../../dashboard/panelPreviewContext'
import { toIso } from '../../../../format'
import { FieldGrid, LabeledDate, LabeledSelect, LabeledText, LabeledValue } from '../../../fieldPrimitives'

export type EligibilityCheck = AdvancedFinancialEligibilityCheckFragment

type LookupInputs = {
  source: string
  memberID: string
  groupNumber: string
  firstname: string
  lastname: string
  birthDate: string
}

export const isoDay = (value: string | Date | null | undefined) => toIso(value)?.slice(0, 10) ?? ''

export const aperoEligibilityUrl = (eligibilityID: string) => `https://app.aperohealth.com/eligibility/${eligibilityID}`

const asString = (value: unknown) => (typeof value === 'string' ? value : '')

// Mirrors Standard's lookup form: the latest check's subscriber first, else what onboarding stored on the patient.
const initialInputs = (patient: GetAdvancedEligibilityLookupInputsQuery['patient'] | undefined): LookupInputs => {
  const details = (patient?.details ?? {}) as Record<string, unknown>
  const stored = (details.insuranceSubscriber ?? {}) as Record<string, unknown>
  const subscriber = patient?.financialEligibilityCheck?.subscriber

  if (subscriber) {
    return {
      source: asString(details.source),
      memberID: subscriber.id ?? '',
      groupNumber: subscriber.groupNumber ?? '',
      firstname: subscriber.firstName ?? '',
      lastname: subscriber.lastName ?? '',
      birthDate: isoDay(subscriber.birthDate) || isoDay(patient?.birthDate),
    }
  }

  return {
    source: asString(details.source),
    memberID: asString(stored.memberID) || asString(details.memberID),
    groupNumber: asString(stored.groupNumber) || asString(details.groupID),
    firstname: asString(stored.firstname) || (patient?.firstname ?? ''),
    lastname: asString(stored.lastname) || (patient?.lastname ?? ''),
    birthDate: asString(stored.birthDate) || isoDay(patient?.birthDate),
  }
}

const CheckResult = ({ check }: { check: EligibilityCheck }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <FieldGrid>
      <LabeledValue label="Member ID" value={check.subscriber.id || '—'} />
      <LabeledValue label="Group number" value={check.subscriber.groupNumber || '—'} />
      <LabeledValue
        label="Name"
        value={[check.subscriber.firstName, check.subscriber.lastName].filter(Boolean).join(' ') || '—'}
      />
      <LabeledValue label="Birthdate" value={isoDay(check.subscriber.birthDate) || '—'} />
      <LabeledValue label="Coverage active" value={check.coverageActive ? 'Yes' : 'No'} />
    </FieldGrid>
    {Boolean(check.warnings?.length) && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {check.warnings?.map((warning) => (
          <Chip key={warning} label={warning} color="warning" size="small" />
        ))}
      </Box>
    )}
    <Link href={aperoEligibilityUrl(check.aperoEligibilityID)} target="_blank" rel="noreferrer">
      Open details in Apero
    </Link>
  </Box>
)

const LookupForm = ({
  uid,
  initial,
  sources,
  latestCheck,
  onClose,
  onFill,
}: {
  uid: string
  initial: LookupInputs
  sources: string[]
  latestCheck: EligibilityCheck | undefined
  onClose: () => void
  onFill: (check: EligibilityCheck) => void
}) => {
  const [inputs, setInputs] = useState(initial)
  const [check, setCheck] = useState(latestCheck)
  const [lookup, { loading, error }] = useAdvancedFinancialEligibilityLookupMutation()
  const set = (key: keyof LookupInputs) => (value: string) => setInputs((current) => ({ ...current, [key]: value }))
  const complete = Boolean(
    inputs.source && inputs.memberID.trim() && inputs.firstname.trim() && inputs.lastname.trim() && inputs.birthDate
  )
  const lookupDisabled = useDisabledInPreview(!complete || loading)

  const onLookup = async () => {
    try {
      const { data } = await lookup({
        variables: { uid, ...inputs, groupNumber: inputs.groupNumber.trim() || undefined },
      })
      if (data) {
        setCheck(data.financialEligibilityLookup)
      }
    } catch {
      // The mutation's `error` already carries what the dialog shows.
    }
  }

  return (
    <>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <DialogContentText>
          These values are only used for the lookup itself. Press “Fill form” to use the result for the eligibility
          record.
        </DialogContentText>
        <FieldGrid>
          <LabeledSelect
            label="Source"
            value={inputs.source}
            onChange={set('source')}
            options={sources.map((source) => ({ value: source, label: source }))}
            required
          />
          <LabeledText label="Member ID" value={inputs.memberID} onChange={set('memberID')} required />
          <LabeledText label="Group number" value={inputs.groupNumber} onChange={set('groupNumber')} />
          <LabeledText label="First name" value={inputs.firstname} onChange={set('firstname')} required />
          <LabeledText label="Last name" value={inputs.lastname} onChange={set('lastname')} required />
          <LabeledDate label="Birthdate" value={inputs.birthDate} onChange={set('birthDate')} required />
        </FieldGrid>
        <Box>
          <Button variant="outlined" onClick={onLookup} disabled={lookupDisabled}>
            {loading ? 'Looking up…' : 'Perform lookup'}
          </Button>
        </Box>
        {error && (
          <Alert severity="error">
            <FailureMessage failure={describeFailure('Failed to perform the eligibility lookup.', error)} />
          </Alert>
        )}
        {check && <CheckResult check={check} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!check} onClick={() => check && onFill(check)}>
          Fill form
        </Button>
      </DialogActions>
    </>
  )
}

const LookupContent = ({
  uid,
  onClose,
  onFill,
}: {
  uid: string
  onClose: () => void
  onFill: (check: EligibilityCheck) => void
}) => {
  // `all`: a refused patient read still leaves the sources, and the lookup usable without its prefill.
  const { data, loading } = useGetAdvancedEligibilityLookupInputsQuery({ variables: { uid }, errorPolicy: 'all' })

  if (loading) {
    return (
      <DialogContent>
        <PrefillLoading />
      </DialogContent>
    )
  }

  return (
    <LookupForm
      uid={uid}
      initial={initialInputs(data?.patient)}
      sources={data?.sources.map(({ name }) => name) ?? []}
      latestCheck={data?.patient?.financialEligibilityCheck ?? undefined}
      onClose={onClose}
      onFill={onFill}
    />
  )
}

const HealthPlanLookupDialog = ({
  uid,
  open,
  onClose,
  onFill,
}: {
  uid: string
  open: boolean
  onClose: () => void
  onFill: (check: EligibilityCheck) => void
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogHeader title="Apero lookup tool" onClose={onClose} />
    {open && <LookupContent uid={uid} onClose={onClose} onFill={onFill} />}
  </Dialog>
)

export default HealthPlanLookupDialog
